import { useMemo, useState, type ReactNode } from "react";
import { styled } from "@pigment-css/react";
import dayjs from "dayjs";
import { useTranslations } from "@acto/web-common/locales";
import { MeetingScope, RecurrenceCadence } from "@acto/gql/graphql";
import { UNLIMITED_MEETINGS, useMeetings } from "@acto/taskpane/core/data-access/meetings/useMeetings";
import { useViewer } from "@acto/taskpane/core/contexts/ViewerContext";
import {
  addDaysUtc,
  CalendarGrid,
  CalendarHeader,
  todayUtc,
  toIsoDate,
  useCalendarWeek,
} from "@acto/taskpane/field-sales-2/components/primitives/Calendar";
import { Divider } from "@acto/taskpane/field-sales-2/components/primitives/Divider";
import { PlannedMeetings, type ColleagueMeeting } from "./PlannedMeetings";
import { RepeatField } from "./RepeatField";
import { TimeRange } from "./TimeRange";

const Card = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[24],
  marginInline: theme.fieldSalesTwo.spacing[12],
  padding: `${theme.fieldSalesTwo.spacing[24]} ${theme.fieldSalesTwo.spacing[20]}`,
  borderRadius: theme.fieldSalesTwo.radius[38],
  background: theme.fieldSalesTwo.colors.surface.glass,
}));

const WINDOW_WEEKS_BACK = 2;
const WINDOW_WEEKS_FORWARD = 10;

/** Adds one hour to an `HH:mm` string, clamping at end-of-day instead of wrapping past midnight. */
export function plusOneHour(time: string): string {
  const [hours, minutes] = time.split(":").map(Number);
  const total = hours * 60 + minutes + 60;
  if (total >= 24 * 60) return "23:59";

  return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
}

export function defaultMeetingTimes(): { from: string; to: string } {
  const roundedMinute = Math.ceil(dayjs().minute() / 15) * 15;
  const start = dayjs().minute(roundedMinute).second(0);
  const latestStart = dayjs().hour(23).minute(0).second(0);
  const clampedStart = start.isAfter(latestStart) ? latestStart : start;

  const from = clampedStart.format("HH:mm");
  return { from, to: plusOneHour(from) };
}

export interface MeetingFormState {
  date: Date;
  times: { from: string; to: string };
  repeat: boolean;
  cadence: RecurrenceCadence;
  isValid: boolean;
}

interface MeetingFormProps {
  initialDate?: Date;
  /** When set, the day is not selectable: no day grid renders and this is the meeting's day. */
  fixedDate?: Date;
  initialTimes?: { from: string; to: string };
  initialRepeat?: boolean;
  initialCadence?: RecurrenceCadence;
  /** The customer this form is planning for; without it, colleagues' meetings there are not listed. */
  clientId?: string;
  /** Rendered after the form card, so a sticky footer lands outside it. */
  children: (form: MeetingFormState) => ReactNode;
}

export function MeetingForm({
  initialDate,
  fixedDate,
  initialTimes,
  initialRepeat = false,
  initialCadence = RecurrenceCadence.Weekly,
  clientId,
  children,
}: MeetingFormProps) {
  const t = useTranslations("fieldSalesTwo.planMeeting");

  const [repeat, setRepeat] = useState(initialRepeat);
  const [cadence, setCadence] = useState<RecurrenceCadence>(initialCadence);
  const initialSelectedDate = fixedDate ?? initialDate ?? todayUtc();
  // One-off meetings stay back-datable; a series never starts in the past.
  const calendar = useCalendarWeek({
    defaultSelectedDate: initialSelectedDate,
    minDate: repeat ? todayUtc() : undefined,
  });
  const [times, setTimes] = useState(initialTimes ?? defaultMeetingTimes);

  const selectedDate = fixedDate ?? calendar.selectedDate ?? initialSelectedDate;
  // HH:mm strings are zero-padded, so a lexical compare is a valid time compare.
  const isValidRange = Boolean(times.from && times.to && times.to > times.from);
  // The selected day can predate minDate after toggling repeat on; keep the CTA in sync with the disabled cell.
  const isValidDate = !repeat || selectedDate.getTime() >= todayUtc().getTime();
  // A series never starts in the past, and a fixed day cannot be moved out of it, so that pairing
  // would only ever reach an invalid form.
  const canRepeat = !fixedDate || fixedDate.getTime() >= todayUtc().getTime();

  // One 12-week occurrence window (virtual series occurrences included) serves the day markers and
  // day lists for all navigation inside it. The anchor re-snaps only when the visible week exits
  // the window (one refetch), adjusting state during render as in usePagination.
  const weekDays = calendar.weeks[0];
  const weekStart = weekDays[0].date;
  const weekEnd = weekDays[weekDays.length - 1].date;
  const [windowAnchor, setWindowAnchor] = useState(weekStart);
  const windowStart = addDaysUtc(windowAnchor, -WINDOW_WEEKS_BACK * 7);
  const windowEnd = addDaysUtc(windowAnchor, WINDOW_WEEKS_FORWARD * 7 - 1);
  if (weekStart.getTime() < windowStart.getTime() || weekEnd.getTime() > windowEnd.getTime()) {
    setWindowAnchor(weekStart);
  }

  const { isViewer, colleagueName } = useViewer();
  const windowRange = fixedDate
    ? { startDate: toIsoDate(fixedDate), endDate: toIsoDate(fixedDate) }
    : { startDate: toIsoDate(windowStart), endDate: toIsoDate(windowEnd) };

  const { data: ownMeetings } = useMeetings({ dateRange: windowRange, limit: UNLIMITED_MEETINGS });

  // A colleague's meeting at another customer is never fetched.
  const { data: clientMeetings } = useMeetings({
    dateRange: windowRange,
    clientId,
    limit: UNLIMITED_MEETINGS,
    scope: MeetingScope.ClientAccess,
    enabled: Boolean(clientId),
  });

  const colleagueMeetings = useMemo<ColleagueMeeting[]>(
    () =>
      (clientMeetings ?? [])
        .filter((meeting) => !isViewer(meeting.owner))
        .map((meeting) => ({ meeting, ownerName: colleagueName(meeting.owner) })),
    [clientMeetings, isViewer, colleagueName],
  );

  // Marked days must match what the day list can actually show, or a dot leads to an empty list.
  const meetingDays = useMemo(
    () =>
      new Set(
        [...(ownMeetings ?? []), ...colleagueMeetings.map((entry) => entry.meeting)].map((meeting) =>
          meeting.scheduledDate.format("YYYY-MM-DD"),
        ),
      ),
    [ownMeetings, colleagueMeetings],
  );

  const markedWeeks = calendar.weeks.map((week) =>
    week.map((day) => ({ ...day, hasMeeting: meetingDays.has(toIsoDate(day.date)) })),
  );

  const dayMeetings = (ownMeetings ?? []).filter(
    (meeting) => meeting.scheduledDate.format("YYYY-MM-DD") === toIsoDate(selectedDate),
  );

  const dayColleagueMeetings = colleagueMeetings.filter(
    (entry) => entry.meeting.scheduledDate.format("YYYY-MM-DD") === toIsoDate(selectedDate),
  );

  return (
    <>
      <Card>
        {!fixedDate && (
          <>
            <CalendarHeader
              title={calendar.title}
              previousLabel={t("previousWeek")}
              nextLabel={t("nextWeek")}
              onPrevious={calendar.goToPrevious}
              onNext={calendar.goToNext}
            />
            <Divider />
            <CalendarGrid weekdayLabels={calendar.weekdayLabels} weeks={markedWeeks} onSelectDay={calendar.selectDay} />
          </>
        )}
        <PlannedMeetings meetings={dayMeetings} colleagueMeetings={dayColleagueMeetings} />
        <TimeRange
          fromTime={times.from}
          toTime={times.to}
          // Moving the start always re-slots the meeting to one hour; only the end field is free-form.
          onFromChange={(from) => setTimes({ from, to: plusOneHour(from) })}
          onToChange={(to) => setTimes((current) => ({ ...current, to }))}
        />
        <Divider />
        <RepeatField
          repeat={repeat}
          onRepeatChange={canRepeat ? setRepeat : undefined}
          cadence={cadence}
          onCadenceChange={setCadence}
        />
      </Card>
      {children({ date: selectedDate, times, repeat, cadence, isValid: isValidRange && isValidDate })}
    </>
  );
}
